import React, { useEffect, useState } from 'react';
import { Outlet, Navigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import Navbar from '../components/common/Navbar';
import Sidebar from '../components/common/Sidebar';
import Footer from '../components/common/Footer';
import theme from '../styles/theme';
import { useResponsive } from '../hooks/useResponsive';

/**
 * Layout for authenticated ERP pages (/app/*).
 * Handles sidebar state, mobile overlay, and auth guard.
 */
const ERPLayout = ({ onLoginClick }) => {
  const { isAuthenticated, isLoading, user } = useAuth();
  const { isMobile, isTablet } = useResponsive();
  // On mobile and tablet the sidebar is an overlay drawer so the content area
  // gets the full width (the inline 250px sidebar would push the toolbar off-screen).
  const isDrawerMode = isMobile || isTablet;
  const [isSidebarOpen, setIsSidebarOpen] = useState(false);

  useEffect(() => {
    if (!isDrawerMode) setIsSidebarOpen(false);
  }, [isDrawerMode]);

  useEffect(() => {
    if (!isDrawerMode) return;
    document.body.style.overflow = isSidebarOpen ? 'hidden' : '';
    return () => { document.body.style.overflow = ''; };
  }, [isDrawerMode, isSidebarOpen]);

  useEffect(() => {
    if (!isDrawerMode || !isSidebarOpen) return;
    const handleEscape = (e) => { if (e.key === 'Escape') setIsSidebarOpen(false); };
    document.addEventListener('keydown', handleEscape);
    return () => document.removeEventListener('keydown', handleEscape);
  }, [isDrawerMode, isSidebarOpen]);

  const styles = {
    shell: {
      display: 'flex',
      flexDirection: 'column',
      minHeight: '100vh',
      backgroundColor: theme.colors.background.default,
      fontFamily: theme.typography.fontFamily.primary,
    },
    mainLayout: {
      display: 'flex',
      flex: 1,
      position: 'relative',
    },
    contentArea: {
      flex: 1,
      padding: isMobile ? theme.spacing.md : theme.spacing.xl,
      backgroundColor: theme.colors.background.default,
      minWidth: 0,
      overflowX: 'hidden',
    },
    sidebarBackdrop: {
      position: 'fixed',
      inset: 0,
      backgroundColor: 'rgba(13, 26, 40, 0.45)',
      zIndex: 1000,
    },
    loadingShell: {
      minHeight: '100vh',
      display: 'grid',
      placeItems: 'center',
      padding: isMobile ? theme.spacing.lg : theme.spacing.xl,
      background: 'linear-gradient(135deg, #f7fafc 0%, #e8f1f7 55%, #ddebf6 100%)',
      fontFamily: theme.typography.fontFamily.primary,
    },
    loadingCard: {
      width: '100%',
      maxWidth: '560px',
      borderRadius: '18px',
      padding: isMobile ? theme.spacing.lg : theme.spacing.xl,
      backgroundColor: '#ffffff',
      boxShadow: '0 16px 48px rgba(13, 26, 40, 0.12)',
      border: '1px solid rgba(13, 26, 40, 0.08)',
      display: 'flex',
      flexDirection: 'column',
      gap: theme.spacing.md,
    },
    loadingTitle: {
      margin: 0,
      color: theme.colors.text.primary,
      fontSize: isMobile ? theme.typography.fontSize.lg : theme.typography.fontSize.xl,
      fontWeight: theme.typography.fontWeight.bold,
      letterSpacing: '0.01em',
    },
    loadingSubtitle: {
      margin: 0,
      color: theme.colors.text.secondary,
      fontSize: theme.typography.fontSize.sm,
      lineHeight: 1.6,
    },
    loadingBar: {
      width: '100%',
      height: '10px',
      borderRadius: '999px',
      background: 'linear-gradient(90deg, rgba(32, 86, 135, 0.22) 0%, rgba(32, 86, 135, 0.55) 50%, rgba(32, 86, 135, 0.22) 100%)',
      backgroundSize: '200% 100%',
      animation: 'erpAuthLoadingWave 1.4s ease-in-out infinite',
    },
    loadingSkeletonGrid: {
      display: 'grid',
      gridTemplateColumns: isMobile ? '1fr' : '1fr 1fr',
      gap: theme.spacing.sm,
      marginTop: theme.spacing.xs,
    },
    loadingSkeletonItem: {
      height: '44px',
      borderRadius: '12px',
      backgroundColor: 'rgba(32, 86, 135, 0.08)',
      animation: 'erpAuthLoadingPulse 1.6s ease-in-out infinite',
    },
  };

  // Wait until auth state is restored from storage before deciding redirects.
  if (isLoading) {
    return (
      <div style={styles.loadingShell}>
        <style>
          {`@keyframes erpAuthLoadingWave {
              0% { background-position: 200% 0; }
              100% { background-position: -200% 0; }
            }
            @keyframes erpAuthLoadingPulse {
              0%, 100% { opacity: 0.55; }
              50% { opacity: 1; }
            }`}
        </style>
        <div style={styles.loadingCard} role="status" aria-live="polite" aria-label="Restoring session">
          <h2 style={styles.loadingTitle}>Restoring your workspace</h2>
          <p style={styles.loadingSubtitle}>Checking your session and preparing your private dashboard...</p>
          <div style={styles.loadingBar} />
          <div style={styles.loadingSkeletonGrid}>
            <div style={styles.loadingSkeletonItem} />
            <div style={styles.loadingSkeletonItem} />
            <div style={styles.loadingSkeletonItem} />
            <div style={styles.loadingSkeletonItem} />
          </div>
        </div>
      </div>
    );
  }

  // Redirect to home if not authenticated (after all hooks)
  if (!isAuthenticated) {
    return <Navigate to="/" replace />;
  }

  if (user?.onboardingPending) {
    return <Navigate to="/register/verify" replace />;
  }

  return (
    <div style={styles.shell}>
      <Navbar onLoginClick={onLoginClick} onMenuClick={() => setIsSidebarOpen((p) => !p)} />
      <div style={styles.mainLayout}>
        {!isDrawerMode && <Sidebar />}
        {isDrawerMode && (
          <>
            {isSidebarOpen && (
              <div style={styles.sidebarBackdrop} onClick={() => setIsSidebarOpen(false)} />
            )}
            <Sidebar isMobile isOpen={isSidebarOpen} onClose={() => setIsSidebarOpen(false)} />
          </>
        )}
        <div style={styles.contentArea}>
          <Outlet />
        </div>
      </div>
      <Footer />
    </div>
  );
};

export default ERPLayout;
